(function (global) {
    'use strict';

    const RETRYABLE_STATUSES = new Set([429]);

    function isRetryableStatus(status) {
        return RETRYABLE_STATUSES.has(status) || status >= 500;
    }

    function wait(milliseconds) {
        return new Promise(resolve => setTimeout(resolve, milliseconds));
    }

    function pageUrl(baseUrl, page) {
        const url = new URL(baseUrl, global.location && global.location.href ? global.location.href : 'https://love-rugs-uk.myshopify.com');
        if (page === 1) {
            url.searchParams.delete('page');
        } else {
            url.searchParams.set('page', String(page));
        }
        return url.toString();
    }

    async function fetchPage(url, fetchImpl, retryLimit, retryDelayMs, attempt = 0) {
        let response;
        try {
            response = await fetchImpl(url);
        } catch (error) {
            if (attempt < retryLimit) {
                await wait(retryDelayMs * (attempt + 1));
                return fetchPage(url, fetchImpl, retryLimit, retryDelayMs, attempt + 1);
            }
            throw error;
        }

        const status = Number(response.status || 0);
        const failed = response.ok === false || (status && (status < 200 || status >= 300));
        if (failed) {
            if (isRetryableStatus(status) && attempt < retryLimit) {
                await wait(retryDelayMs * (attempt + 1));
                return fetchPage(url, fetchImpl, retryLimit, retryDelayMs, attempt + 1);
            }
            throw new Error(`Collection JSON request failed: ${status || 'unknown status'}`);
        }

        let payload;
        try {
            payload = await response.json();
        } catch (error) {
            if (attempt < retryLimit) {
                await wait(retryDelayMs * (attempt + 1));
                return fetchPage(url, fetchImpl, retryLimit, retryDelayMs, attempt + 1);
            }
            throw new Error('Collection JSON response was not valid JSON');
        }

        if (!payload || !Array.isArray(payload.products)) {
            throw new Error('Collection JSON did not contain products');
        }
        return payload;
    }

    async function loadCollectionPages({
        url,
        fetchImpl = global.fetch,
        retryLimit = 2,
        retryDelayMs = 500
    }) {
        if (typeof fetchImpl !== 'function') throw new Error('Collection loader requires fetch');

        const firstPage = await fetchPage(url, fetchImpl, retryLimit, retryDelayMs);
        const totalPages = Number(firstPage.total_pages);
        const currentPage = Number(firstPage.current_page || 1);
        if (!Number.isInteger(totalPages) || totalPages < 1) {
            throw new Error('Collection JSON response had an invalid page count');
        }
        if (currentPage !== 1) {
            throw new Error('Collection JSON page one response had an invalid page number');
        }

        const pages = new Array(totalPages);
        pages[0] = firstPage;
        await Promise.all(Array.from({length: totalPages - 1}, (_, index) => index + 2).map(async page => {
            const payload = await fetchPage(pageUrl(url, page), fetchImpl, retryLimit, retryDelayMs);
            if (Number(payload.current_page) !== page) {
                throw new Error(`Collection JSON response had an invalid page number: expected ${page}`);
            }
            pages[page - 1] = payload;
        }));

        return {
            totalPages,
            pages,
            products: pages.flatMap(payload => payload.products)
        };
    }

    function values(value) {
        return Array.isArray(value) ? value : [];
    }

    function numericValue(value) {
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        if (typeof value !== 'string') return null;

        const normalised = value.trim().replace(/,/g, '').replace(/^[£$€]/, '');
        if (!normalised || !/^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalised)) return null;

        const number = Number(normalised);
        return Number.isFinite(number) ? number : null;
    }

    function selectedMetricVariants(product) {
        const variants = values(product && product.variants).filter(Boolean);
        const available = variants.filter(variant => Boolean(variant.available));
        return available.length ? available : variants;
    }

    function productSortMetrics(product) {
        const variants = selectedMetricVariants(product);
        const prices = variants
            .map(variant => numericValue(variant.price))
            .filter(value => value != null);
        const areas = variants
            .map(variant => {
                const length = numericValue(variant.size && variant.size.length);
                const width = numericValue(variant.size && variant.size.width);
                return length != null && width != null && length > 0 && width > 0 ? length * width : null;
            })
            .filter(value => value != null);
        const salePairs = values(product && product.variants)
            .filter(variant => variant && Boolean(variant.available))
            .map(variant => {
                const price = numericValue(variant.price);
                const compareAt = numericValue(variant.compare_at_price);
                if (price == null || compareAt == null || compareAt <= price) return null;
                return {
                    amount: compareAt - price,
                    percent: ((compareAt - price) / compareAt) * 100
                };
            })
            .filter(Boolean);

        return {
            startingPrice: prices.length ? Math.min(...prices) : null,
            highestPrice: prices.length ? Math.max(...prices) : null,
            discountPercent: salePairs.length ? Math.max(...salePairs.map(pair => pair.percent)) : 0,
            discountAmount: salePairs.length ? Math.max(...salePairs.map(pair => pair.amount)) : 0,
            minArea: areas.length ? Math.min(...areas) : null,
            maxArea: areas.length ? Math.max(...areas) : null
        };
    }

    function compareSortValues(left, right, descending = false) {
        if (left == null && right == null) return 0;
        if (left == null) return 1;
        if (right == null) return -1;
        const comparison = left - right;
        return descending ? -comparison : comparison;
    }

    function productDate(product) {
        const raw = product && (product.date_added ?? product.created_at ?? product.published_at);
        const time = raw == null ? NaN : Date.parse(String(raw));
        return Number.isFinite(time) ? time : null;
    }

    function productSortRank(product, property) {
        return numericValue(product && product[property]);
    }

    function compareSortProducts(left, right, sortBy, {
        baseRankProperty = 'relevanceRank',
        bestSellingRankProperty = 'bestSellingRank'
    } = {}) {
        const sort = String(sortBy || 'best-match').toLowerCase();
        const leftMetrics = productSortMetrics(left);
        const rightMetrics = productSortMetrics(right);
        let comparison = 0;

        if (sort === 'best-selling') {
            comparison = compareSortValues(
                productSortRank(left, bestSellingRankProperty),
                productSortRank(right, bestSellingRankProperty)
            );
        } else if (sort === 'new' || sort === 'newest' || sort === 'created-descending') {
            comparison = compareSortValues(productDate(left), productDate(right), true);
        } else if (sort === 'low-to-high' || sort === 'price-ascending' || sort === 'price-asc') {
            comparison = compareSortValues(leftMetrics.startingPrice, rightMetrics.startingPrice);
        } else if (sort === 'high-to-low' || sort === 'price-descending' || sort === 'price-desc') {
            comparison = compareSortValues(leftMetrics.highestPrice, rightMetrics.highestPrice, true);
        } else if (sort === 'discount-high-to-low') {
            comparison = compareSortValues(leftMetrics.discountPercent, rightMetrics.discountPercent, true);
        } else if (sort === 'discount-amount-high-to-low') {
            comparison = compareSortValues(leftMetrics.discountAmount, rightMetrics.discountAmount, true);
        } else if (sort === 'size-large-small') {
            comparison = compareSortValues(leftMetrics.maxArea, rightMetrics.maxArea, true);
        } else if (sort === 'size-small-large') {
            comparison = compareSortValues(leftMetrics.minArea, rightMetrics.minArea);
        }

        return comparison || compareSortValues(
            productSortRank(left, sort === 'best-selling' ? bestSellingRankProperty : baseRankProperty),
            productSortRank(right, sort === 'best-selling' ? bestSellingRankProperty : baseRankProperty)
        );
    }

    function hasAny(needles, haystack) {
        return values(needles).some(needle => values(haystack).includes(needle));
    }

    function variantIsAvailable(variant, hideOutOfStockItems) {
        return !hideOutOfStockItems || Boolean(variant && variant.available);
    }

    function variantMatchesSize(product, size, hideOutOfStockItems) {
        const sizeChecks = {
            xxs: variant => variant.size.length < 150 && variant.size.width < 90,
            xs: variant => variant.size.length > 130 && variant.size.length <= 160 && variant.size.width > 70 && variant.size.width <= 110,
            s: variant => variant.size.length > 160 && variant.size.length <= 185 && variant.size.width > 90 && variant.size.width <= 120,
            m: variant => variant.size.length > 185 && variant.size.length <= 210 && variant.size.width > 120 && variant.size.width <= 150,
            l: variant => variant.size.length > 210 && variant.size.length <= 250 && variant.size.width > 140 && variant.size.width <= 170,
            xl: variant => variant.size.length > 250 && variant.size.length <= 320 && variant.size.width > 170 && variant.size.width <= 200,
            xxl: variant => variant.size.length > 320 && variant.size.length <= 360 && variant.size.width > 200 && variant.size.width <= 270,
            xxxl: variant => variant.size.length > 360 && variant.size.length <= 400 && variant.size.width > 270 && variant.size.width <= 300,
            'above-all': variant => variant.size.length > 400 && variant.size.width > 300,
            hr: () => String(product.shape || '').includes('Runner')
        };
        const check = sizeChecks[size];
        return Boolean(check && values(product.variants).some(variant => variant && variant.size && check(variant) && variantIsAvailable(variant, hideOutOfStockItems)));
    }

    function sizeInputMatches(product, inputs, hideOutOfStockItems) {
        if (!inputs || !inputs.active) return true;
        return values(product.variants).some(variant => {
            if (!variant || !variant.size) return false;
            return variant.size.length >= inputs.min_length &&
                variant.size.length <= inputs.max_length &&
                variant.size.width >= inputs.min_width &&
                variant.size.width <= inputs.max_width &&
                variantIsAvailable(variant, hideOutOfStockItems);
        });
    }

    function colourScore(product, selectedColours, anyColourMatch) {
        const selected = values(selectedColours).slice(0, 3);
        if (!selected.length) return undefined;

        const colours = values(product.colours).filter(colour => colour && colour !== 'null');
        const primary = product.colours && product.colours[0];
        const matchedCount = selected.filter(colour => colours.includes(colour)).length;
        const primaryMatches = selected.includes(primary);

        if (selected.length === 1) {
            return primaryMatches ? colours.length : null;
        }

        if (anyColourMatch) {
            return primaryMatches ? colours.length : null;
        }

        if (matchedCount < (selected.length === 3 ? 2 : selected.length)) return null;
        if (selected.length === 3) return matchedCount === 3 ? 1 : 2;
        return matchedCount === 2 && colours.length === 2 ? 1 : 2;
    }

    function matchesProduct(product, state, hideOutOfStockItems) {
        if (hideOutOfStockItems && !values(product.variants).some(variant => variantIsAvailable(variant, true))) return false;
        if (values(state.custom).length && !values(state.custom).includes(product.customisable)) return false;
        if (values(state.sale).length && !values(state.sale).includes(product.sale)) return false;
        if (values(state.category).length && !values(state.category).includes(product.categories)) return false;
        if (values(state.pattern).length && !hasAny(state.pattern, product.patterns)) return false;
        if (values(state.fabric).length && !values(state.fabric).includes(product.main_fabric)) return false;
        if (values(state.construction).length && !values(state.construction).includes(product.construction)) return false;
        if (values(state.shape).length && !values(state.shape).includes(product.shape)) return false;
        if (values(state.design).length && !values(state.design).includes(product.designer)) return false;
        if (values(state.size).length && !values(state.size).some(size => variantMatchesSize(product, size, hideOutOfStockItems))) return false;
        if (!sizeInputMatches(product, state.inputs, hideOutOfStockItems)) return false;
        return true;
    }

    function filterAndRank(products, state = {}, {hideOutOfStockItems = false} = {}) {
        const source = values(products);
        const filtered = source
            .map((product, index) => ({product, index, colourRank: colourScore(product, state.colour, state.anyColourMatch)}))
            .filter(item => item.colourRank !== null && matchesProduct(item.product, state, hideOutOfStockItems));

        if (!values(state.colour).length) return filtered.map(item => item.product);

        return filtered
            .sort((a, b) => a.colourRank - b.colourRank || a.index - b.index)
            .map(item => item.product);
    }

    function restoreBestMatch(sourceProducts, state = {}, options = {}) {
        return filterAndRank(values(sourceProducts), state, options).slice();
    }

    const SUPPORTED_SORT_VALUES = new Set([
        'best-match',
        'best-selling',
        'new',
        'low-to-high',
        'high-to-low',
        'discount-high-to-low',
        'discount-amount-high-to-low',
        'size-large-small',
        'size-small-large'
    ]);

    function normalizeCollectionDefaultSort(defaultSort) {
        return String(defaultSort || '').toLowerCase() === 'best-selling' ? 'best-selling' : 'best-match';
    }

    function collectionPresentationState({variantFiltered = false, status = 'loading'} = {}) {
        if (!variantFiltered) {
            return {
                useServerGrid: true,
                showVariantLoader: false,
                activateClientGrid: false
            };
        }

        return {
            useServerGrid: false,
            showVariantLoader: status === 'loading',
            activateClientGrid: status === 'ready'
        };
    }

    function collectionLoadMoreStep({variantFiltered = false} = {}) {
        return variantFiltered ? 40 : 50;
    }

    function createPendingAction(action = {}) {
        if (action.type === 'sort') {
            return Object.freeze({
                type: 'sort',
                sort: String(action.sort || ''),
                customerInitiated: Boolean(action.customerInitiated)
            });
        }

        if (action.type === 'filter') {
            return Object.freeze({
                type: 'filter',
                element: action.element == null ? null : String(action.element),
                attribute: action.attribute == null ? null : String(action.attribute),
                value: action.value == null ? null : String(action.value),
                targetTagName: action.targetTagName == null ? null : String(action.targetTagName),
                targetChecked: Boolean(action.targetChecked),
                currentTargetChecked: Boolean(action.currentTargetChecked)
            });
        }

        throw new Error(`Unknown pending collection action: ${action.type || 'missing type'}`);
    }

    function enqueuePendingAction(queue, action) {
        return Object.freeze([...values(queue), createPendingAction(action)]);
    }

    function drainPendingActions(queue, replay) {
        const actions = Object.freeze(values(queue).slice());
        if (typeof replay === 'function') actions.forEach(replay);
        return actions;
    }

    function resolveCollectionSort({
        currentSort,
        defaultSort = 'best-selling',
        sortChosenByCustomer = false,
        isFilterInteraction = false
    } = {}) {
        if (isFilterInteraction && !sortChosenByCustomer) return 'best-match';
        return SUPPORTED_SORT_VALUES.has(currentSort) ? currentSort : normalizeCollectionDefaultSort(defaultSort);
    }

    function filterStateKey(collectionHandle) {
        return `lrCollectionFilters:${collectionHandle}:v1`;
    }

    function readFilterState(storage, collectionHandle) {
        if (!storage || !collectionHandle) return null;
        try {
            const raw = storage.getItem(filterStateKey(collectionHandle));
            return raw ? JSON.parse(raw) : null;
        } catch (error) {
            return null;
        }
    }

    function readRestorableFilterState(storage, collectionHandle, performanceRef) {
        if (!performanceRef) return null;

        try {
            const entries = typeof performanceRef.getEntriesByType === 'function'
                ? performanceRef.getEntriesByType('navigation')
                : [];
            const navigationType = entries && entries[0] && entries[0].type;
            const isBackForward = navigationType
                ? navigationType === 'back_forward'
                : Number(performanceRef.navigation && performanceRef.navigation.type) === 2;

            return isBackForward ? readFilterState(storage, collectionHandle) : null;
        } catch (error) {
            return null;
        }
    }

    function writeFilterState(storage, collectionHandle, state) {
        if (!storage || !collectionHandle) return;
        storage.setItem(filterStateKey(collectionHandle), JSON.stringify(state));
    }

    function clearLegacyState(storage) {
        if (!storage) return;
        storage.removeItem('products');
        storage.removeItem('choices');
    }

    const api = {
        loadCollectionPages,
        productSortMetrics,
        compareSortValues,
        compareSortProducts,
        filterAndRank,
        restoreBestMatch,
        normalizeCollectionDefaultSort,
        collectionPresentationState,
        collectionLoadMoreStep,
        createPendingAction,
        enqueuePendingAction,
        drainPendingActions,
        resolveCollectionSort,
        readFilterState,
        readRestorableFilterState,
        writeFilterState,
        clearLegacyState,
        filterStateKey
    };

    global.LrCollectionFilters = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
